import { Checkbox } from '@immo/ui-kit';
import type { PermissionScopeGroup } from '@/lib/permissions/catalogue';

/**
 * The grouped, searchable, per-group select-all permission picker shared by the
 * admin API-key issue dialog and the edit dialog (IMM-493.2).
 *
 * Extracted from `pages/api-keys/new.tsx` rather than copied: two permission
 * pickers drift, and the dangerous direction is a catalogue that silently stops
 * matching the grantable allow-list.
 *
 * `groups` is an explicit PROP, not a module-level constant read from the call
 * site — the closure over `PERMISSION_GROUPS` in `new.tsx` could not travel with
 * the component. Deriving the catalogue internally would work today but would
 * bake in the assumption that every consumer wants the full admin catalogue.
 */

export type PermissionCatalogueProps = {
  readonly groups: ReadonlyArray<PermissionScopeGroup>;
  readonly query: string;
  readonly selected: ReadonlyArray<string>;
  readonly disabled: boolean;
  readonly label: (permission: string) => string;
  // The identifier shown next to the label, or `null` when the label already IS
  // the literal. A PROP rather than an internal `usePermissionLiteral()` call
  // for the same reason `label` is: the component does not assume its consumer
  // reads the shared catalogue (IMM-620.1).
  readonly literal: (permission: string) => string | null;
  readonly moduleLabel: (module: string) => string;
  readonly scopeLabel: (scope: string) => string;
  readonly selectAllLabel: string;
  readonly deselectAllLabel: string;
  readonly emptyLabel: string;
  readonly onToggle: (permission: string) => void;
  readonly onSetGroup: (permissions: ReadonlyArray<string>, selected: boolean) => void;
};

export function PermissionCatalogue({
  groups,
  query,
  selected,
  disabled,
  label,
  literal,
  moduleLabel,
  scopeLabel,
  selectAllLabel,
  deselectAllLabel,
  emptyLabel,
  onToggle,
  onSetGroup,
}: PermissionCatalogueProps) {
  const normalised = query.trim().toLowerCase();

  const matches = (permission: string): boolean => {
    if (normalised === '') return true;

    return permission.toLowerCase().includes(normalised) || label(permission).toLowerCase().includes(normalised);
  };

  const visibleGroups = groups
    .map((group) => ({
      scope: group.scope,
      modules: group.modules
        .map((module) => ({ module: module.module, permissions: module.permissions.filter(matches) }))
        .filter((module) => module.permissions.length > 0),
    }))
    .filter((group) => group.modules.length > 0);

  if (visibleGroups.length === 0) {
    return <p className="px-1 py-2 text-sm text-text-tertiary">{emptyLabel}</p>;
  }

  return (
    <div className="max-h-72 space-y-4 overflow-y-auto rounded-md border border-border p-3">
      {visibleGroups.map((group) => (
        <div key={group.scope} className="space-y-3">
          <p className="text-sm font-semibold uppercase tracking-wide text-text-secondary">{scopeLabel(group.scope)}</p>
          {group.modules.map((module) => {
            const allSelected = module.permissions.every((permission) => selected.includes(permission));

            return (
              <div key={`${group.scope}:${module.module}`} className="space-y-1 pl-1">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-text-tertiary">{moduleLabel(module.module)}</p>
                  <button
                    type="button"
                    className="text-sm text-interactive focus-visible:shadow-focus focus-visible:outline-none disabled:opacity-50"
                    disabled={disabled}
                    onClick={() => onSetGroup(module.permissions, !allSelected)}
                  >
                    {allSelected ? deselectAllLabel : selectAllLabel}
                  </button>
                </div>
                {module.permissions.map((permission) => {
                  const identifier = literal(permission);

                  return (
                    <div key={permission} className="flex items-center gap-2 text-base-sm text-text-primary">
                      <label className="flex items-center gap-2">
                        <Checkbox
                          checked={selected.includes(permission)}
                          disabled={disabled}
                          onCheckedChange={() => onToggle(permission)}
                        />
                        <span>{label(permission)}</span>
                      </label>
                      {/* The identifier the API docs specify, beside the prose
                          that explains it (IMM-620.1) — an operator issuing a
                          key on a customer's behalf works from those tokens.
                          OUTSIDE the `<label>` on purpose: the checkbox is
                          addressed by its prose name everywhere, and the search
                          box above already matches the literal, so the row stays
                          findable either way. */}
                      {identifier !== null && (
                        <code className="font-mono text-sm text-text-tertiary">{identifier}</code>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}
